<template>

  <!-- slim: there is only room for a numbered circle -->
  <a
    v-if="slim"
    href="javascript:void(0)"
    class="conversation-item conversation-item-slim d-flex justify-content-center py-1"
    :title="displayAddress"
    @click="$emit('select', conversation._id)">
    <span
      class="slim-circle rounded-circle d-flex align-items-center justify-content-center"
      :class="active ? 'bg-primary text-white' : 'bg-light text-body'">
      {{ index + 1 }}
      <span
        v-if="isUnread"
        class="unread-dot rounded-circle bg-primary"></span>
    </span>
  </a>

  <a
    v-else
    href="javascript:void(0)"
    class="conversation-item d-flex align-items-center p-3 border-bottom text-body"
    :class="{ 'bg-light': active }"
    @click="$emit('select', conversation._id)">

    <Avatar
      class="mr-3 flex-shrink-0"
      :src="photo"
      :name="displayName"
      size="44px"
      radius="22px" />

    <div class="conversation-item-body flex-grow-1">

      <!-- top line: name + time of the last message -->
      <div class="d-flex align-items-center justify-content-between">
        <span
          class="text-truncate"
          :class="{ 'font-weight-bold': isUnread }">{{ displayName }}</span>
        <small
          v-if="lastMessageAt"
          class="text-muted flex-shrink-0 ml-2">{{ relativeTime }}</small>
      </div>

      <!-- bottom line: message preview + unread dot -->
      <div class="d-flex align-items-center justify-content-between">
        <small
          class="text-truncate"
          :class="isUnread ? 'text-body' : 'text-muted'">
          <i
            v-if="isOutbound"
            class="fa-solid fa-reply mr-1 text-muted"></i>{{ preview }}
        </small>
        <span
          v-if="isUnread"
          class="unread-dot rounded-circle bg-primary flex-shrink-0 ml-2"></span>
      </div>

    </div>

  </a>

</template>

<script>
import config from '@/config'
import Avatar from '@/components/Parts/Avatar'

export default {
  name: 'ConversationListItem',
  components: {
    Avatar
  },
  props: {
    conversation: {
      type: Object,
      required: true
    },
    active: {
      type: Boolean,
      default: false
    },
    slim: {
      type: Boolean,
      default: false
    },
    // position in the list: the number shown on the slim circle
    index: {
      type: Number,
      default: 0
    }
  },
  computed: {
    displayName () {
      return this.conversation.name || this.conversation.contact_address || this.$t('No name')
    },
    // the slim circle has no room for a name: it goes to the item title
    displayAddress () {
      return this.conversation.contact_address || this.conversation.name || this.$t('No name')
    },
    // the only picture of a conversation is the one of the associated candidate
    photo () {
      const candidate = this.conversation.candidate_id
      const url = candidate && candidate.photo_media_url
      return url && !url.includes(config.default_image_url.candidate) ? url : null
    },
    isUnread () {
      return this.conversation.is_read === false
    },
    lastMessage () {
      return this.conversation.last_message || null
    },
    isOutbound () {
      return this.lastMessage && this.lastMessage.direction === 'outbound'
    },
    lastMessageAt () {
      return this.lastMessage && this.lastMessage.sent_at
    },
    preview () {
      if (this.lastMessage && this.lastMessage.summary) {
        return this.lastMessage.summary
      }
      return this.$t('No messages yet')
    },
    relativeTime () {
      if (!this.lastMessageAt) {
        return ''
      }
      return this.$options.filters.moment(this.lastMessageAt, 'fromNow', this.$i18n.locale)
    }
  }
}
</script>

<style scoped>
.conversation-item {
  cursor: pointer;
  text-decoration: none;
}

.conversation-item:hover {
  background-color: #f8f9fa;
  text-decoration: none;
}

/* flex items default to min-width auto and never shrink below their content,
   so every level down to the truncating element needs min-width 0 */
.conversation-item-body,
.conversation-item-body > div,
.conversation-item-body .text-truncate {
  min-width: 0;
}

.unread-dot {
  width: 9px;
  height: 9px;
}

.conversation-item-slim:hover {
  background-color: transparent;
}

.slim-circle {
  position: relative;
  width: 36px;
  height: 36px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #dee2e6;
}

.conversation-item-slim:hover .slim-circle {
  border-color: #adb5bd;
}

/* on the circle, the unread mark sits on its border, top right */
.slim-circle .unread-dot {
  position: absolute;
  top: -1px;
  right: -1px;
  border: 1px solid #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/Messaging/sidebar/ConversationListItem.vue